import AppLoader from 'Components/AppLoader';
import { memo, useEffect } from 'preact/compat';

import { loadPage } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { clearPostAuthDestination, getPostAuthDestination } from '@/shared/postAuthDestination';

const GuestOnlyGuard = ({ redirectRoute, fallback, children, overrideHistory }) => {
  const logged = useSelector((state) => state.user.logged);
  const sessionChecked = useSelector((state) => state.user.sessionChecked);
  const isSpeedyFlowOpen = useSelector((state) => !!state.modals.speedyFlow?.visible);
  const hasFallback = !!fallback;

  useEffect(() => {
    if (!logged || !sessionChecked || hasFallback) {
      return undefined;
    }

    // Not while the Speedy Flow is running. A visitor who signs up *inside* the modal becomes
    // logged-in on a guest-only route, which is exactly what this guard exists to move along — so
    // it would navigate out from under a flow that is mid-journey, taking the verification step
    // with it. The flow closes itself when it is done, and this runs then: `isSpeedyFlowOpen` is a
    // dependency, so the redirect happens the moment it is safe to.
    if (isSpeedyFlowOpen) {
      return undefined;
    }

    const newPath = getPostAuthDestination();
    if (!overrideHistory && newPath) {
      const newPathURL = new URL(newPath, window.location.origin);
      clearPostAuthDestination();
      loadPage(`${newPathURL.pathname}${newPathURL.search}`);
      return undefined;
    }
    const redirectRouteURL = new URL(redirectRoute || newPath || '/home', window.location.origin);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('ml'); // remove magic link token
    if (['/map', '/home', '/schedule'].includes(redirectRouteURL.pathname) && searchParams.size > 0) {
      searchParams.forEach((value, key) => {
        redirectRouteURL.searchParams.set(key, value);
      });
    }
    // here we use loadPage(window.open or window.location) instead of route
    // is due that the main routes don't exist in the public routing pages
    clearPostAuthDestination();
    loadPage(`${redirectRouteURL.pathname}${redirectRouteURL.search}`);
    return undefined;
  }, [logged, sessionChecked, hasFallback, redirectRoute, overrideHistory, isSpeedyFlowOpen]);

  if (sessionChecked) {
    return logged ? fallback || <AppLoader /> : children;
  }

  return <AppLoader />;
};

export default memo(GuestOnlyGuard);
